'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { MessageCircle, Heart, Reply, ChevronDown, ChevronUp, Send, Sparkles, UserCircle } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface Comment {
  id: string;
  outfitId: string;
  authorId: string | null;
  parentId: string | null;
  content: string;
  createdAt: string;
  authorName: string | null;
  likeCount: number;
  isLikedByMe: boolean;
  isCreatorComment: boolean;
  replies: Comment[];
}

interface MoodBoardCommentsProps {
  outfitId: string;
  creatorId: string | null;
  /** compact = collapsed by default (used in feed cards) */
  compact?: boolean;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

function CommentItem({
  comment,
  creatorId,
  currentUserId,
  onLike,
  onReply,
  depth,
}: {
  comment: Comment;
  creatorId: string | null;
  currentUserId: string | null;
  onLike: (commentId: string, isLiked: boolean) => void;
  onReply: (commentId: string, authorName: string | null) => void;
  depth: number;
}) {
  const [showReplies, setShowReplies] = useState(depth === 0 && comment.replies.length <= 3);
  const isCreator = comment.isCreatorComment;

  return (
    <div className={depth > 0 ? 'ml-8 mt-3' : 'mt-4'}>
      <div
        className={`rounded-xl p-3.5 ${isCreator ? 'border' : ''}`}
        style={{
          backgroundColor: isCreator ? 'rgba(201,169,110,0.06)' : 'hsl(35 15% 97%)',
          borderColor: isCreator ? 'rgba(201,169,110,0.25)' : 'transparent',
        }}
      >
        {/* Author row */}
        <div className="flex items-center gap-2 mb-2">
          <div
            className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0"
            style={{ backgroundColor: isCreator ? 'rgba(201,169,110,0.2)' : 'hsl(35 15% 90%)', color: isCreator ? '#C9A96E' : 'hsl(30 8% 52%)' }}
          >
            {comment.authorName ? comment.authorName.charAt(0).toUpperCase() : <UserCircle size={12} />}
          </div>

          {comment.authorId ? (
            <Link
              href={`/creator/${comment.authorId}`}
              className="text-xs font-semibold hover:opacity-70 transition-opacity"
              style={{ color: isCreator ? '#C9A96E' : '#1A1A1A' }}
            >
              {comment.authorName ?? 'Anonymous'}
            </Link>
          ) : (
            <span className="text-xs font-semibold" style={{ color: 'hsl(30 8% 52%)' }}>
              {comment.authorName ?? 'Anonymous'}
            </span>
          )}

          {isCreator && (
            <span
              className="flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-full"
              style={{ backgroundColor: 'rgba(201,169,110,0.15)', color: '#C9A96E' }}
            >
              <Sparkles size={9} />
              Creator
            </span>
          )}

          <span className="text-xs ml-auto" style={{ color: 'hsl(30 8% 62%)' }}>
            {timeAgo(comment.createdAt)}
          </span>
        </div>

        {/* Content */}
        <p className="text-sm leading-relaxed" style={{ color: '#1A1A1A' }}>
          {comment.content}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-3 mt-2.5">
          <button
            onClick={() => onLike(comment.id, comment.isLikedByMe)}
            className="flex items-center gap-1 text-xs transition-all hover:opacity-70"
            style={{ color: comment.isLikedByMe ? '#C9A96E' : 'hsl(30 8% 52%)' }}
            aria-label={comment.isLikedByMe ? 'Unlike comment' : 'Like comment'}
          >
            <Heart
              size={12}
              fill={comment.isLikedByMe ? '#C9A96E' : 'none'}
              style={{ color: comment.isLikedByMe ? '#C9A96E' : 'hsl(30 8% 52%)' }}
            />
            {comment.likeCount > 0 && <span>{comment.likeCount}</span>}
          </button>

          {depth < 2 && (
            <button
              onClick={() => onReply(comment.id, comment.authorName)}
              className="flex items-center gap-1 text-xs transition-all hover:opacity-70"
              style={{ color: 'hsl(30 8% 52%)' }}
              aria-label="Reply to comment"
            >
              <Reply size={12} />
              Reply
            </button>
          )}
        </div>
      </div>

      {/* Replies */}
      {comment.replies.length > 0 && (
        <div>
          {!showReplies ? (
            <button
              onClick={() => setShowReplies(true)}
              className="flex items-center gap-1 mt-2 ml-8 text-xs transition-all hover:opacity-70"
              style={{ color: '#C9A96E' }}
            >
              <ChevronDown size={12} />
              {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
            </button>
          ) : (
            <>
              {comment.replies.map(reply => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  creatorId={creatorId}
                  currentUserId={currentUserId}
                  onLike={onLike}
                  onReply={onReply}
                  depth={depth + 1}
                />
              ))}
              {comment.replies.length > 1 && (
                <button
                  onClick={() => setShowReplies(false)}
                  className="flex items-center gap-1 mt-2 ml-8 text-xs transition-all hover:opacity-70"
                  style={{ color: 'hsl(30 8% 52%)' }}
                >
                  <ChevronUp size={12} />
                  Hide replies
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function MoodBoardComments({ outfitId, creatorId, compact = false }: MoodBoardCommentsProps) {
  const { user } = useAuth();
  const router = useRouter();
  const supabase = createClient();

  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(!compact);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyTo, setReplyTo] = useState<{ id: string; name: string | null } | null>(null);
  const [totalCount, setTotalCount] = useState(0);

  const buildTree = useCallback(
    (flat: Comment[]): Comment[] => {
      const map = new Map<string, Comment>();
      flat.forEach(c => map.set(c.id, { ...c, replies: [] }));
      const roots: Comment[] = [];
      map.forEach(c => {
        if (c.parentId && map.has(c.parentId)) {
          map.get(c.parentId)!.replies.push(c);
        } else {
          roots.push(c);
        }
      });
      return roots;
    },
    []
  );

  const fetchComments = useCallback(async () => {
    setLoading(true);
    try {
      const { data: rawComments } = await supabase
        .from('mood_board_comments')
        .select('id, outfit_id, author_id, parent_id, content, created_at')
        .eq('outfit_id', outfitId)
        .order('created_at', { ascending: true });

      if (!rawComments || rawComments.length === 0) {
        setComments([]);
        setTotalCount(0);
        return;
      }

      // Fetch like counts
      const commentIds = rawComments.map((c: any) => c.id);
      const { data: likeCounts } = await supabase
        .from('mood_board_comment_like_counts')
        .select('comment_id, like_count')
        .in('comment_id', commentIds);

      const likeCountMap: Record<string, number> = {};
      (likeCounts ?? []).forEach((r: any) => {
        likeCountMap[r.comment_id] = Number(r.like_count);
      });

      // Fetch my likes
      let myLikes = new Set<string>();
      if (user) {
        const { data: myLikeData } = await supabase
          .from('mood_board_comment_likes')
          .select('comment_id')
          .eq('user_id', user.id)
          .in('comment_id', commentIds);
        (myLikeData ?? []).forEach((r: any) => myLikes.add(r.comment_id));
      }

      // Fetch author names
      const authorIds = [...new Set(rawComments.map((c: any) => c.author_id).filter(Boolean))];
      let authorNames: Record<string, string> = {};
      if (authorIds.length > 0) {
        const { data: profileData } = await supabase
          .from('creator_profiles')
          .select('id, display_name')
          .in('id', authorIds);
        (profileData ?? []).forEach((p: any) => {
          authorNames[p.id] = p.display_name;
        });
      }

      const flat: Comment[] = rawComments.map((c: any) => ({
        id: c.id,
        outfitId: c.outfit_id,
        authorId: c.author_id ?? null,
        parentId: c.parent_id ?? null,
        content: c.content,
        createdAt: c.created_at,
        authorName: c.author_id ? (authorNames[c.author_id] ?? 'Creator') : null,
        likeCount: likeCountMap[c.id] ?? 0,
        isLikedByMe: myLikes.has(c.id),
        isCreatorComment: c.author_id === creatorId,
        replies: [],
      }));

      setTotalCount(flat.length);
      setComments(buildTree(flat));
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [outfitId, creatorId, user, supabase, buildTree]);

  useEffect(() => {
    if (expanded) {
      fetchComments();
    }
  }, [expanded, fetchComments]);

  const handleLike = async (commentId: string, isLiked: boolean) => {
    if (!user) {
      router.push('/login');
      return;
    }
    // Optimistic update
    const updateLike = (list: Comment[]): Comment[] =>
      list.map(c => {
        if (c.id === commentId) {
          return {
            ...c,
            isLikedByMe: !isLiked,
            likeCount: isLiked ? c.likeCount - 1 : c.likeCount + 1,
          };
        }
        return { ...c, replies: updateLike(c.replies) };
      });
    setComments(prev => updateLike(prev));

    if (isLiked) {
      await supabase
        .from('mood_board_comment_likes')
        .delete()
        .eq('comment_id', commentId)
        .eq('user_id', user.id);
    } else {
      await supabase
        .from('mood_board_comment_likes')
        .insert({ comment_id: commentId, user_id: user.id });
    }
  };

  const handleReply = (commentId: string, authorName: string | null) => {
    if (!user) {
      router.push('/login');
      return;
    }
    setReplyTo({ id: commentId, name: authorName });
    setNewComment('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      router.push('/login');
      return;
    }
    const trimmed = newComment.trim();
    if (!trimmed) return;

    setSubmitting(true);
    try {
      await supabase.from('mood_board_comments').insert({
        outfit_id: outfitId,
        author_id: user.id,
        parent_id: replyTo?.id ?? null,
        content: trimmed,
      });
      setNewComment('');
      setReplyTo(null);
      await fetchComments();
    } catch {
      // silently fail
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="border-t" style={{ borderColor: 'hsl(35 20% 92%)' }}>
      {/* Toggle header */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-5 py-3 text-xs font-medium transition-all hover:opacity-70"
        style={{ color: 'hsl(30 8% 52%)' }}
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-1.5">
          <MessageCircle size={13} style={{ color: '#C9A96E' }} />
          {totalCount > 0 ? `${totalCount} comment${totalCount !== 1 ? 's' : ''}` : 'Comments'}
        </span>
        {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
      </button>

      {expanded && (
        <div className="px-5 pb-5">
          {/* Comment list */}
          {loading ? (
            <div className="space-y-3 mb-4">
              {[1, 2].map(i => (
                <div key={i} className="animate-pulse rounded-xl p-3.5" style={{ backgroundColor: 'hsl(35 15% 94%)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full" style={{ backgroundColor: 'hsl(35 15% 88%)' }} />
                    <div className="h-3 rounded w-24" style={{ backgroundColor: 'hsl(35 15% 88%)' }} />
                  </div>
                  <div className="h-3 rounded w-3/4" style={{ backgroundColor: 'hsl(35 15% 88%)' }} />
                </div>
              ))}
            </div>
          ) : comments.length === 0 ? (
            <p className="text-xs py-3 mb-3" style={{ color: 'hsl(30 8% 62%)' }}>
              No comments yet. Be the first to share your thoughts!
            </p>
          ) : (
            <div className="mb-4">
              {comments.map(c => (
                <CommentItem
                  key={c.id}
                  comment={c}
                  creatorId={creatorId}
                  currentUserId={user?.id ?? null}
                  onLike={handleLike}
                  onReply={handleReply}
                  depth={0}
                />
              ))}
            </div>
          )}

          {/* Comment input */}
          <form onSubmit={handleSubmit} className="mt-2">
            {replyTo && (
              <div
                className="flex items-center justify-between rounded-lg px-3 py-2 mb-2 text-xs"
                style={{ backgroundColor: 'rgba(201,169,110,0.08)', color: '#C9A96E' }}
              >
                <span>Replying to <strong>{replyTo.name ?? 'comment'}</strong></span>
                <button
                  type="button"
                  onClick={() => setReplyTo(null)}
                  className="hover:opacity-70 transition-opacity font-medium"
                >
                  Cancel
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder={user ? 'Add a comment…' : 'Sign in to comment'}
                disabled={!user || submitting}
                maxLength={500}
                className="flex-1 rounded-xl px-3.5 py-2.5 text-xs border outline-none transition-all focus:ring-1 disabled:opacity-50"
                style={{
                  backgroundColor: 'hsl(35 15% 97%)',
                  borderColor: 'hsl(35 20% 88%)',
                  color: '#1A1A1A',
                }}
                onFocus={e => {
                  e.currentTarget.style.borderColor = '#C9A96E';
                  e.currentTarget.style.boxShadow = '0 0 0 2px rgba(201,169,110,0.15)';
                }}
                onBlur={e => {
                  e.currentTarget.style.borderColor = 'hsl(35 20% 88%)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onClick={() => {
                  if (!user) router.push('/login');
                }}
              />
              <button
                type="submit"
                disabled={!user || !newComment.trim() || submitting}
                className="flex items-center justify-center w-9 h-9 rounded-xl transition-all hover:opacity-80 disabled:opacity-40 flex-shrink-0"
                style={{ backgroundColor: '#1A1A1A', color: '#FAF7F2' }}
                aria-label="Post comment"
              >
                <Send size={13} />
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
